import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  CreditCardIcon, 
  TrashIcon, 
  StarIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  BoltIcon,
  ClipboardDocumentIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

// ============ BINs REALES DE BANCOS ============
const BIN_DATABASE = {
  visa: [
    { bin: '418839', bank: 'BANCO DE BOGOTA', country: 'CO' },
    { bin: '453294', bank: 'BANCOLOMBIA', country: 'CO' },
    { bin: '453288', bank: 'DAVIVIENDA', country: 'CO' },
    { bin: '471612', bank: 'BBVA COLOMBIA', country: 'CO' },
    { bin: '402400', bank: 'BANCO DE CREDITO', country: 'PE' },
    { bin: '448590', bank: 'SCOTIABANK', country: 'PE' },
    { bin: '492181', bank: 'BANCO GENERAL', country: 'PA' },
    { bin: '491648', bank: 'BAC CREDOMATIC', country: 'NI' }
  ],
  mastercard: [
    { bin: '512345', bank: 'BANCO DE BOGOTA', country: 'CO' },
    { bin: '522234', bank: 'BANCOLOMBIA', country: 'CO' },
    { bin: '525452', bank: 'DAVIVIENDA', country: 'CO' },
    { bin: '529272', bank: 'BBVA COLOMBIA', country: 'CO' },
    { bin: '532435', bank: 'SCOTIABANK', country: 'PE' },
    { bin: '535368', bank: 'BANCO DE CREDITO', country: 'PE' },
    { bin: '542418', bank: 'BANCO GENERAL', country: 'PA' },
    { bin: '545054', bank: 'BAC CREDOMATIC', country: 'NI' }
  ],
  amex: [
    { bin: '340000', bank: 'AMERICAN EXPRESS', country: 'US' },
    { bin: '370000', bank: 'AMERICAN EXPRESS', country: 'US' },
    { bin: '374200', bank: 'AMERICAN EXPRESS MEXICO', country: 'MX' },
    { bin: '376682', bank: 'AMERICAN EXPRESS BRASIL', country: 'BR' },
    { bin: '377572', bank: 'AMERICAN EXPRESS ARGENTINA', country: 'AR' },
    { bin: '378282', bank: 'AMERICAN EXPRESS CHILE', country: 'CL' }
  ],
  discover: [
    { bin: '601100', bank: 'DISCOVER BANK', country: 'US' },
    { bin: '601109', bank: 'DISCOVER BANK', country: 'US' },
    { bin: '601111', bank: 'DISCOVER BANK', country: 'US' },
    { bin: '601122', bank: 'DISCOVER BANK', country: 'US' },
    { bin: '601133', bank: 'DISCOVER BANK', country: 'US' }
  ]
};

// Nombres realistas
const CARDHOLDER_NAMES = [
  'JUAN CARLOS PEREZ GONZALEZ', 'MARIA FERNANDA GOMEZ RODRIGUEZ', 'CARLOS ANDRES RUIZ MARTINEZ',
  'ANA SOFIA MARTINEZ LOPEZ', 'LUIS ALBERTO FERNANDEZ DIAZ', 'LAURA VALENTINA DIAZ SANCHEZ',
  'PEDRO JOSE SANCHEZ RAMIREZ', 'SOFIA ISABEL RAMIREZ TORRES', 'DIEGO ALEJANDRO TORRES FLORES',
  'VALENTINA CAMILA FLORES VARGAS', 'SEBASTIAN MANUEL VARGAS CASTRO', 'NATALIA PAZ CASTRO MENDOZA'
];

// ============ FUNCIONES DE MÁSCARA ============

// Generar valor a partir de máscara (reemplaza x/X con dígitos aleatorios)
const applyMask = (mask, fallback = null) => {
  if (!mask || !mask.includes('x') && !mask.includes('X')) {
    return mask || fallback;
  }
  
  let result = '';
  for (let i = 0; i < mask.length; i++) {
    const char = mask[i];
    if (char === 'x' || char === 'X') {
      result += secureRandom(0, 9);
    } else if (char !== ' ' && char !== '-') {
      result += char;
    }
  }
  return result;
};

// Generador criptográfico
const secureRandom = (min, max) => {
  const range = max - min + 1;
  const randomBytes = new Uint32Array(1);
  crypto.getRandomValues(randomBytes);
  return min + (randomBytes[0] % range);
};

// Algoritmo de Luhn
const calculateLuhnDigit = (partialNumber) => {
  let sum = 0;
  let shouldDouble = true;
  
  for (let i = partialNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(partialNumber[i]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  
  return (10 - (sum % 10)) % 10;
};

// Generar número de tarjeta con máscara o BIN
const generateCardNumber = (brand, numberMask = null) => {
  let baseNumber = '';
  
  if (numberMask && (numberMask.includes('x') || numberMask.includes('X'))) {
    baseNumber = applyMask(numberMask);
  } else if (numberMask && numberMask.length >= 15) {
    baseNumber = numberMask;
  } else {
    const bins = BIN_DATABASE[brand];
    const binData = bins[secureRandom(0, bins.length - 1)];
    baseNumber = binData.bin;
  }
  
  // Asegurar 15 dígitos antes del checksum
  while (baseNumber.length < 15) {
    baseNumber += secureRandom(0, 9);
  }
  if (baseNumber.length > 15) {
    baseNumber = baseNumber.slice(0, 15);
  }
  
  const checkDigit = calculateLuhnDigit(baseNumber);
  return baseNumber + checkDigit;
};

// Generar fecha a partir de máscara o aleatoria
const generateExpiryDate = (monthMask = null, yearMask = null) => {
  let month, year, year2, year4;
  
  // Generar mes
  if (monthMask && (monthMask.includes('x') || monthMask.includes('X'))) {
    month = applyMask(monthMask);
    if (month.length === 1) month = '0' + month;
    if (parseInt(month) > 12) month = '12';
    if (parseInt(month) < 1) month = '01';
  } else if (monthMask && monthMask.length >= 2) {
    month = monthMask.slice(0, 2);
    if (parseInt(month) > 12) month = '12';
    if (parseInt(month) < 1) month = '01';
  } else {
    const randomMonth = secureRandom(1, 12);
    month = randomMonth < 10 ? `0${randomMonth}` : `${randomMonth}`;
  }
  
  // Generar año
  const currentYear = new Date().getFullYear();
  if (yearMask && (yearMask.includes('x') || yearMask.includes('X'))) {
    let yearRaw = applyMask(yearMask);
    if (yearRaw.length === 2) {
      year2 = yearRaw;
      year4 = `20${yearRaw}`;
    } else if (yearRaw.length === 4) {
      year4 = yearRaw;
      year2 = yearRaw.slice(-2);
    } else {
      year2 = '25';
      year4 = '2025';
    }
  } else if (yearMask && yearMask.length === 2) {
    year2 = yearMask;
    year4 = `20${yearMask}`;
  } else if (yearMask && yearMask.length === 4) {
    year4 = yearMask;
    year2 = yearMask.slice(-2);
  } else {
    const randomYear = secureRandom(currentYear + 1, currentYear + 5);
    year2 = randomYear.toString().slice(-2);
    year4 = randomYear.toString();
  }
  
  return { month, year2, year4 };
};

// Generar CVV a partir de máscara
const generateCVV = (brand, cvvMask = null) => {
  let cvv;
  
  if (cvvMask && (cvvMask.includes('x') || cvvMask.includes('X'))) {
    cvv = applyMask(cvvMask);
  } else if (cvvMask && (cvvMask.length === 3 || cvvMask.length === 4)) {
    cvv = cvvMask;
  } else {
    const length = brand === 'amex' ? 4 : 3;
    cvv = '';
    for (let i = 0; i < length; i++) {
      cvv += secureRandom(0, 9);
    }
  }
  
  return cvv;
};

// Generar nombre
const generateName = () => {
  return CARDHOLDER_NAMES[secureRandom(0, CARDHOLDER_NAMES.length - 1)];
};

// Obtener información del banco
const getBankInfo = (cardNumber, brand) => {
  const bin = cardNumber.slice(0, 6);
  const banks = BIN_DATABASE[brand];
  const bankData = banks.find(b => b.bin === bin);
  return bankData || { bank: 'BANCO DESCONOCIDO', country: 'XX' };
};

// Validar Luhn
const validateLuhn = (number) => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

function CardManager() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorCount, setGeneratorCount] = useState(5);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [batchFormat, setBatchFormat] = useState('csv');
  
  // Máscaras
  const [numberMask, setNumberMask] = useState('');
  const [monthMask, setMonthMask] = useState('');
  const [yearMask, setYearMask] = useState('');
  const [cvvMask, setCvvMask] = useState('');
  const [useMasks, setUseMasks] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('visa');
  
  const [manualCard, setManualCard] = useState({
    number: '',
    month: '',
    year: '',
    cvv: '',
    brand: 'visa',
    name: ''
  });
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery('cards', async () => {
    const response = await api.get('/cards');
    return response.data.cards;
  });

  const deleteCardMutation = useMutation(
    (cardId) => api.delete(`/cards/${cardId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cards');
        queryClient.invalidateQueries('stats');
        toast.success('🗑️ Tarjeta eliminada');
        refetch();
      },
      onError: () => toast.error('❌ Error al eliminar tarjeta')
    }
  );

  const deleteMultipleMutation = useMutation(
    async (cardIds) => {
      const promises = cardIds.map(id => api.delete(`/cards/${id}`));
      return Promise.all(promises);
    },
    {
      onSuccess: (results) => {
        queryClient.invalidateQueries('cards');
        queryClient.invalidateQueries('stats');
        toast.success(`✅ ${results.length} tarjetas eliminadas`);
        setSelectedCards(new Set());
        refetch();
      },
      onError: () => toast.error('❌ Error al eliminar tarjetas')
    }
  );

  const addCardMutation = useMutation(
    (cardData) => api.post('/cards', cardData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cards');
        queryClient.invalidateQueries('stats');
        setShowAddForm(false);
        setManualCard({ number: '', month: '', year: '', cvv: '', brand: 'visa', name: '' });
        toast.success('💳 Tarjeta agregada exitosamente');
        refetch();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al agregar tarjeta');
      }
    }
  );

  const setDefaultMutation = useMutation(
    (cardId) => api.put(`/cards/${cardId}`, { isDefault: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cards');
        toast.success('⭐ Tarjeta marcada como predeterminada');
        refetch();
      },
      onError: () => toast.error('❌ Error al marcar como default')
    }
  );

  const generateTestCards = () => {
    setIsGenerating(true);
    const generated = [];
    
    for (let i = 0; i < generatorCount; i++) {
      const brand = selectedBrand;
      
      // Generar número con máscara o BIN
      const cardNumber = generateCardNumber(brand, useMasks ? numberMask : null);
      
      // Generar fecha con máscaras
      const { month, year2, year4 } = generateExpiryDate(
        useMasks ? monthMask : null,
        useMasks ? yearMask : null
      );
      
      // Generar CVV con máscara
      const cvv = generateCVV(brand, useMasks ? cvvMask : null);
      
      const name = generateName();
      const bankInfo = getBankInfo(cardNumber, brand);
      const separator = batchFormat === 'csv' ? ',' : '|';
      
      generated.push({
        number: cardNumber,
        month,
        year: year2,
        yearFull: year4,
        cvv,
        name,
        brand,
        bank: bankInfo.bank,
        country: bankInfo.country,
        isValid: validateLuhn(cardNumber),
        formatLineAA: `${cardNumber}${separator}${month}${separator}${year2}${separator}${cvv}`,
        formatLineAAAA: `${cardNumber}${separator}${month}${separator}${year4}${separator}${cvv}`
      });
    }
    
    setGeneratedCards(generated);
    setIsGenerating(false);
    toast.success(`🎲 ${generatorCount} tarjetas generadas`, { duration: 4000 });
  };

  const addGeneratedCards = async () => {
    if (generatedCards.length === 0) {
      toast.error('No hay tarjetas generadas');
      return;
    }
    
    toast.loading(`Agregando ${generatedCards.length} tarjetas...`, { id: 'add-gen' });
    
    try {
      const promises = generatedCards.map(card =>
        api.post('/cards', {
          cardNumber: card.number,
          expiryMonth: card.month,
          expiryYear: card.year,
          cvv: card.cvv,
          cardholderName: card.name,
          brand: card.brand,
          cardType: 'credit'
        })
      );
      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.data?.success !== false).length;
      toast.success(`✅ ${successCount} tarjetas agregadas`, { id: 'add-gen' });
      queryClient.invalidateQueries('cards');
      queryClient.invalidateQueries('stats');
      setGeneratedCards([]);
      setShowGenerator(false);
      refetch();
    } catch (error) {
      toast.error('❌ Error al agregar tarjetas', { id: 'add-gen' });
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    
    let year = manualCard.year;
    if (year.length === 4) year = year.slice(-2);
    
    if (manualCard.cvv.length !== 3 && manualCard.cvv.length !== 4) {
      toast.error('CVV debe tener 3 o 4 dígitos');
      return;
    }
    
    if (manualCard.number.length !== 16) {
      toast.error('Número de tarjeta debe tener 16 dígitos');
      return;
    }
    
    if (!validateLuhn(manualCard.number)) {
      toast.error('Número de tarjeta inválido (falló verificación)');
      return;
    }
    
    addCardMutation.mutate({
      cardNumber: manualCard.number,
      expiryMonth: manualCard.month.padStart(2, '0'),
      expiryYear: year,
      cvv: manualCard.cvv,
      cardholderName: manualCard.name || `Tarjeta ${manualCard.number.slice(-4)}`,
      brand: manualCard.brand,
      cardType: 'credit'
    });
  };

  const toggleSelectCard = (cardId) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(cardId)) newSelected.delete(cardId);
    else newSelected.add(cardId);
    setSelectedCards(newSelected);
  };

  const selectAll = () => {
    if (!data) return;
    const newSelected = new Set();
    data.forEach(card => newSelected.add(card.id));
    setSelectedCards(newSelected);
  };

  const deselectAll = () => {
    setSelectedCards(new Set());
  };

  const handleDeleteSelected = () => {
    if (selectedCards.size === 0) {
      toast.error('Selecciona al menos una tarjeta');
      return;
    }
    if (window.confirm(`¿Eliminar ${selectedCards.size} tarjeta(s)?`)) {
      deleteMultipleMutation.mutate(Array.from(selectedCards));
    }
  };

  const getCardIcon = (brand) => {
    const icons = { visa: '💳 VISA', mastercard: '💳 MASTERCARD', amex: '💳 AMEX', discover: '💳 DISCOVER' };
    return icons[brand] || '💳';
  };

  const getBrandColor = (brand) => {
    const colors = { visa: '#0066ff', mastercard: '#eb001b', amex: '#2e77bb', discover: '#ff6000' };
    return colors[brand] || '#9ca3af';
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#000000' }}>
        <div className="loader"></div>
      </div>
    );
  }

  const cards = data || [];

  return (
    <div style={{ minHeight: '100vh', background: '#000000', padding: '1rem', paddingBottom: '6rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>💳 Mis Tarjetas</h1>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}> 418839xxxxxx, xx/25, xxx</p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowGenerator(!showGenerator)} style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #ff9900, #ff6600)', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BeakerIcon style={{ width: '1rem', height: '1rem' }} /> Generar Tarjetas
              </button>
              <button onClick={() => setShowAddForm(!showAddForm)} style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #00ffff, #0066ff)', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PlusCircleIcon style={{ width: '1rem', height: '1rem' }} /> Agregar
              </button>
            </div>
          </div>
        </div>

        {/* Generador con máscaras completas */}
        {showGenerator && (
          <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #ff9900', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: '#ff9900', fontWeight: 'bold' }}>🎲 Generador</h3>
              <button onClick={() => setShowGenerator(false)} style={{ color: '#9ca3af', cursor: 'pointer', fontSize: '1.25rem' }}>✕</button>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <label style={{ color: '#ffffff', marginRight: '0.5rem' }}>Marca:</label>
                <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} style={{ padding: '0.5rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}>
                  <option value="visa">Visa</option><option value="mastercard">Mastercard</option><option value="amex">Amex</option><option value="discover">Discover</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#ffffff', marginRight: '0.5rem' }}>Cantidad:</label>
                <select value={generatorCount} onChange={(e) => setGeneratorCount(parseInt(e.target.value))} style={{ padding: '0.5rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}>
                  {[1,2,3,4,5,6,7,8,9,10,15,20,25,30].map(n => <option key={n} value={n}>{n} tarjetas</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <label style={{ color: '#ffffff' }}>Formato:</label>
                <button onClick={() => setBatchFormat('csv')} style={{ padding: '0.25rem 0.75rem', background: batchFormat === 'csv' ? '#00ffff' : '#1a1a1a', border: 'none', borderRadius: '0.5rem', color: batchFormat === 'csv' ? '#000000' : '#9ca3af', cursor: 'pointer' }}>CSV (,)</button>
                <button onClick={() => setBatchFormat('pipe')} style={{ padding: '0.25rem 0.75rem', background: batchFormat === 'pipe' ? '#00ffff' : '#1a1a1a', border: 'none', borderRadius: '0.5rem', color: batchFormat === 'pipe' ? '#000000' : '#9ca3af', cursor: 'pointer' }}>PIPE (|)</button>
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={useMasks} onChange={(e) => setUseMasks(e.target.checked)} style={{ width: '1rem', height: '1rem', accentColor: '#ff9900' }} />
                <span style={{ color: '#ff9900' }}> personalizadas (x = dígito aleatorio)</span>
              </label>
            </div>
            
            {useMasks && (
              <div style={{ background: '#1a1a1a', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
                  <div>
                    <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Número (ej: 418839xxxxxx)</label>
                    <input type="text" placeholder="418839xxxxxx" value={numberMask} onChange={(e) => setNumberMask(e.target.value)} style={{ width: '100%', padding: '0.5rem', background: '#0a0a0a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                  </div>
                  <div>
                    <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>CVV (ej: xxx o xxxx)</label>
                    <input type="text" placeholder="xxx" value={cvvMask} onChange={(e) => setCvvMask(e.target.value)} style={{ width: '100%', padding: '0.5rem', background: '#0a0a0a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                  </div>
                  <div>
                    <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Mes (ej: xx o 01)</label>
                    <input type="text" placeholder="xx" value={monthMask} onChange={(e) => setMonthMask(e.target.value)} style={{ width: '100%', padding: '0.5rem', background: '#0a0a0a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                  </div>
                  <div>
                    <label style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Año (ej: xx, xxxx, 25, 2025)</label>
                    <input type="text" placeholder="xx o xxxx" value={yearMask} onChange={(e) => setYearMask(e.target.value)} style={{ width: '100%', padding: '0.5rem', background: '#0a0a0a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                  </div>
                </div>
                <p style={{ color: '#ff9900', fontSize: '0.7rem', marginTop: '0.5rem' }}>💡 Las 'x' se reemplazan por dígitos aleatorios. Campos vacíos = valores aleatorios.</p>
              </div>
            )}
            
            <div style={{ background: '#1a1a1a', borderRadius: '0.5rem', padding: '0.5rem', marginBottom: '1rem', fontSize: '0.7rem' }}>
              <span style={{ color: '#00ffff' }}>📌 Ejemplo:</span>
              <span style={{ color: '#9ca3af', marginLeft: '0.5rem' }}>Número: 418839xxxxxx | Fecha: xx/25, 01/xxxx, xx/2028 | CVV: xxx</span>
            </div>
            
            <button onClick={generateTestCards} disabled={isGenerating} style={{ width: '100%', padding: '0.75rem', background: isGenerating ? '#666' : '#ff9900', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: isGenerating ? 'not-allowed' : 'pointer', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <BoltIcon style={{ width: '1rem', height: '1rem', animation: isGenerating ? 'spin 1s linear infinite' : 'none' }} />
              {isGenerating ? 'Generando...' : '🎲 Generar Tarjetas'}
            </button>
            
            {generatedCards.length > 0 && (
              <div>
                <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '1rem' }}>
                  {generatedCards.map((card, idx) => (
                    <div key={idx} style={{ background: '#1a1a1a', padding: '0.75rem', borderRadius: '0.5rem', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div>
                          <span style={{ fontFamily: 'monospace', color: '#00ffff' }}>{card.number}</span>
                          <span style={{ color: '#9ca3af', marginLeft: '0.5rem' }}>{card.month}/{card.yearFull}</span>
                          <span style={{ color: card.cvv.length === 3 ? '#ccff00' : '#ff6600', marginLeft: '0.5rem' }}>CVV: {card.cvv}</span>
                          <span style={{ color: '#9ca3af', marginLeft: '0.5rem' }}>{card.brand}</span>
                          {card.isValid && <span style={{ color: '#00ff00', marginLeft: '0.5rem' }}>✓</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => { navigator.clipboard.writeText(card.formatLineAA); toast.success('📋 Copiado (AA)'); }} style={{ color: '#00ffff', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0.5rem' }}>📋 AA</button>
                          <button onClick={() => { navigator.clipboard.writeText(card.formatLineAAAA); toast.success('📋 Copiado (AAAA)'); }} style={{ color: '#ff9900', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem 0.5rem' }}>📋 AAAA</button>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.25rem' }}>🏦 {card.bank} | 👤 {card.name.split(' ')[0]} {card.name.split(' ')[1]}</div>
                    </div>
                  ))}
                </div>
                <button onClick={addGeneratedCards} style={{ width: '100%', padding: '0.75rem', background: 'linear-gradient(135deg, #ff9900, #ff6600)', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: 'pointer' }}>Agregar {generatedCards.length} tarjeta(s) al sistema</button>
              </div>
            )}
          </div>
        )}

        {/* Formulario manual */}
        {showAddForm && (
          <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #00ffff', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>Agregar Tarjeta Manualmente</h3>
            <form onSubmit={handleManualSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                <input type="text" placeholder="Número (16 dígitos)" value={manualCard.number} onChange={(e) => setManualCard({...manualCard, number: e.target.value})} required style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} maxLength="16" />
                <input type="text" placeholder="CVV (3-4 dígitos)" value={manualCard.cvv} onChange={(e) => setManualCard({...manualCard, cvv: e.target.value})} required style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} maxLength="4" />
                <input type="text" placeholder="Mes (MM)" value={manualCard.month} onChange={(e) => setManualCard({...manualCard, month: e.target.value})} required style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} maxLength="2" />
                <input type="text" placeholder="Año (25 o 2025)" value={manualCard.year} onChange={(e) => setManualCard({...manualCard, year: e.target.value})} required style={{ padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                <input type="text" placeholder="Nombre (opcional)" value={manualCard.name} onChange={(e) => setManualCard({...manualCard, name: e.target.value})} style={{ gridColumn: 'span 2', padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }} />
                <select value={manualCard.brand} onChange={(e) => setManualCard({...manualCard, brand: e.target.value})} style={{ gridColumn: 'span 2', padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#ffffff' }}>
                  <option value="visa">Visa</option><option value="mastercard">Mastercard</option><option value="amex">American Express</option><option value="discover">Discover</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="button" onClick={() => setShowAddForm(false)} style={{ flex: 1, padding: '0.75rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={addCardMutation.isLoading} style={{ flex: 1, padding: '0.75rem', background: 'linear-gradient(135deg, #00ffff, #0066ff)', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: 'pointer' }}>{addCardMutation.isLoading ? 'Guardando...' : 'Guardar Tarjeta'}</button>
              </div>
            </form>
          </div>
        )}

        {/* Barra de selección */}
        {cards.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={selectAll} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #00ffff', borderRadius: '0.5rem', color: '#00ffff', cursor: 'pointer' }}>Seleccionar todas ({cards.length})</button>
              <button onClick={deselectAll} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer' }}>Deseleccionar</button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {selectedCards.size > 0 && <button onClick={handleDeleteSelected} style={{ padding: '0.5rem 1rem', background: '#ff0033', border: 'none', borderRadius: '0.5rem', color: '#ffffff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><TrashIcon style={{ width: '1rem' }} /> Eliminar {selectedCards.size} tarjeta(s)</button>}
              <span style={{ color: '#9ca3af' }}>{selectedCards.size} / {cards.length} seleccionadas</span>
            </div>
          </div>
        )}

        {/* Lista de tarjetas */}
        <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #222222', overflow: 'hidden' }}>
          {cards.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
              <CreditCardIcon style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
              <p>No hay tarjetas guardadas</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>personaliza</p>
            </div>
          ) : (
            <ul style={{ listStyle: 'none' }}>
              {cards.map((card) => (
                <li key={card.id} style={{ padding: '1rem', borderBottom: '1px solid #222222', background: selectedCards.has(card.id) ? '#00ffff10' : 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div onClick={() => toggleSelectCard(card.id)} style={{ cursor: 'pointer' }}>
                      {selectedCards.has(card.id) ? <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: '#00ffff' }} /> : <div style={{ width: '1.5rem', height: '1.5rem', border: '2px solid #333333', borderRadius: '50%' }} />}
                    </div>
                    <div style={{ width: '3rem', height: '3rem', borderRadius: '0.75rem', background: `${getBrandColor(card.brand)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CreditCardIcon style={{ width: '1.5rem', height: '1.5rem', color: getBrandColor(card.brand) }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 'bold', color: '#ffffff' }}>{getCardIcon(card.brand)}</span>
                        <span style={{ fontFamily: 'monospace', color: '#00ffff' }}>•••• {card.lastFour}</span>
                        {card.isDefault && <span style={{ background: '#ff990020', color: '#ff9900', padding: '0.25rem 0.5rem', borderRadius: '1rem', fontSize: '0.7rem' }}>⭐ Predeterminada</span>}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>Expira: {card.expiryMonth}/{card.expiryYear} · {card.cardholderName}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {!card.isDefault && <button onClick={() => setDefaultMutation.mutate(card.id)} style={{ background: 'none', border: '1px solid #ff9900', borderRadius: '0.5rem', padding: '0.5rem', color: '#ff9900', cursor: 'pointer' }}><StarIcon style={{ width: '1rem' }} /></button>}
                      <button onClick={() => { if (window.confirm('¿Eliminar esta tarjeta?')) deleteCardMutation.mutate(card.id); }} style={{ background: 'none', border: '1px solid #ff0033', borderRadius: '0.5rem', padding: '0.5rem', color: '#ff0033', cursor: 'pointer' }}><TrashIcon style={{ width: '1rem' }} /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default CardManager;